/**
 * Коды ошибок бэкенда — `extensions.code` GraphQL-ошибки.
 *
 * Зеркало `imgame-backend/src/GraphQL/GQLErrors.php` (сверено с `master` `3fceb939`, 15.10.2026).
 * Бэкенд бросает `new UserError('<англ. текст>', GQLErrors::X)`, код в ответ кладёт
 * `UserErrorCodeListener`. Текст отказа английский и технический, поэтому случай определяют по
 * коду, а пользователю показывают перевод из `BACKEND_ERROR_MESSAGES`.
 *
 * «Не бросается» — константа объявлена, но в `src/` бэкенда нет ни одного броска с ней: такой
 * код не придёт, текст ему не нужен (задача бэкенду — IMGM-1562).
 *
 * Новый код: строка здесь + строка в `backend-error-messages.ts`.
 */
export enum BackendErrorCode {
  // Общие
  ONLY_CAPTAIN_ALLOWED = 5,
  INVALID_DATE = 10,
  /** Повтор того же запроса за 3 с (двойной клик) — разбирает IMGM-1561, текста нет. */
  IDEMPOTENCY = 15,
  NOT_ALLOWED = 403,
  /** Не бросается. */
  UNKNOWN_ERROR = 500,

  // Пользователь 1xxx
  PHONE_NOT_FOUND = 1010,
  USER_NOT_FOUND = 1404,

  // Приглашение 2xxx
  /** Не бросается. */
  INVITE_NOT_FOUND = 2404,
  INVITE_INVALID = 2005,

  // Команда 3xxx
  TEAM_NOT_FOUND = 3404,
  USER_NOT_IN_TEAM = 3005,
  TEAM_ALREADY_HAS_CAPTAIN = 3010,
  CANNOT_DELETE_TEAM = 3020,
  USER_ALREADY_IN_TEAM = 3030,
  TEAM_NOT_IN_CHAMPIONSHIP = 3040,

  // Матч 4xxx
  MATCH_NOT_FOUND = 4404,
  MATCH_INVALID = 4005,
  USER_NOT_IN_APPLICATION = 4010,
  /**
   * Четыре условия, а не одно (`StageMutation.php:88-91`): у матча нет даты, до начала меньше
   * 5 часов **или матч уже прошёл**, есть события, есть посты. Текст бэкенда называет только
   * «less than 5 hours».
   */
  MATCH_CANNOT_DELETE = 4015,
  MATCH_NO_DATE = 4020,

  // Заявка в команду 5xxx
  ENTRY_REQUEST_INVALID = 5005,
  ENTRY_REQUEST_USER_ALREADY_IN_TEAM = 5010,
  /** Не бросается. */
  ENTRY_REQUEST_NOT_FOUND = 5404,

  // Организация (backend `tournament`) 6xxx
  TOURNAMENT_NOT_FOUND = 6404,

  // Турнир (backend `championship`) 7xxx
  CHAMPIONSHIP_NOT_FOUND = 7404,
  /** Не бросается. */
  CANNOT_DELETE_CHAMPIONSHIP = 7010,
  /**
   * Не бросается: `ApplicationLimitExceededException` не входит в `APPLICATION_ERROR_CODES`
   * (`ChampionshipApplicationMutation.php:29-31`), и отказ приходит без кода.
   */
  CHAMPIONSHIP_APPLICATIONS_LIMIT_EXCEEDED = 7015,
  USER_ALREADY_ACCEPTED_IN_ANOTHER_TEAM = 7016,
  CHAMPIONSHIP_NOT_ACTIVE = 7020,

  // Этап 8xxx
  STAGE_NOT_FOUND = 8404,
  /**
   * Один код — несколько причин: четыре отказа генерации матчей и «этап не из этого турнира» у
   * статистики. Общего текста нет: причины генерации различают правила «по тексту» реестра, прочие —
   * `overrides` места вызова.
   */
  STAGE_INVALID = 8005,

  // Тур 9xxx
  ROUND_NOT_FOUND = 9404,
  ROUND_INVALID = 9005,

  TEAM_GROUP_NOT_FOUND = 10404,
  SYMBOLIC_TEAM_NOT_FOUND = 11404,
  TOURNAMENT_BRACKET_NOT_FOUND = 12404,
  NOTIFICATION_NOT_FOUND = 13404,
  TOURNAMENT_PLAN_NOT_FOUND = 14404,
  ADDRESS_NOT_FOUND = 15404,

  // Локация 16xxx
  LOCATION_NOT_FOUND = 16404,
  LOCATION_IS_INVOLVED_IN_STATISTICS = 16005,

  // Соцсети 17xxx
  SOCIAL_MEDIA_NOT_FOUND = 17404,
  INCORRECT_URL_FOR_SOCIAL_MEDIA = 17010,

  // Оплата и подписка 18xxx
  /** Опечатка `SUBSCIPTION` — как в `GQLErrors.php`. */
  BILLING_NO_ACTIVE_SUBSCIPTION = 18005,
  BILLING_NO_ACTIVE_PLAN = 18010,
  BILLING_NO_FEATURE = 18015,
  BILLING_INVALID_AMOUNT = 18020,
  /** Общий для любого провала пополнения: под ним и «оплата картой временно недоступна». */
  BILLING_PAYMENT_FAILED = 18025,
  BILLING_TOPUP_NOT_FOUND = 18030,

  CHAMPIONSHIP_APPLICATION_NOT_FOUND = 19404,

  // Прогнозы 20xxx
  PREDICTION_MATCH_ALREADY_STARTED = 20005,
  PREDICTION_MATCH_HAS_RESULT = 20010,
  PREDICTION_ALREADY_EXISTS = 20015,
  PREDICTION_INVALID_TEAM = 20020,

  // Шаблоны media-manager 21xxx
  MEDIA_MANAGER_TEMPLATE_NOT_FOUND = 21404,
  MEDIA_MANAGER_TEMPLATE_NAME_CONFLICT = 21409,

  // Группы шаблонов 22xxx
  MEDIA_MANAGER_TEMPLATE_GROUP_NOT_FOUND = 22404,
  MEDIA_MANAGER_TEMPLATE_GROUP_NAME_CONFLICT = 22409,
  MEDIA_MANAGER_TEMPLATE_GROUP_NOT_EMPTY = 22412,

  // Медиа-автоматизация 23xxx
  /**
   * Один код — несколько причин: вид картинки не поддерживается, сюжет не из этого турнира,
   * задание не спланировалось, неизвестный статус в фильтре. Общего текста нет.
   */
  MEDIA_KIND_NOT_SUPPORTED = 23400,
  MEDIA_GROUP_NOT_OWNED = 23403,
  MEDIA_JOB_NOT_FOUND = 23404,
  MEDIA_JOB_NOT_READY = 23409,
  MEDIA_JOB_RENDERING = 23410,
  MEDIA_JOB_NOT_DELETABLE = 23411,
  MEDIA_TEMPLATE_KIND_MISSING = 23412,
  /** Не бросается. */
  MEDIA_TEXT_PLACEHOLDER_UNKNOWN = 23422,
  MEDIA_TIMEZONE_INVALID = 23423,

  // Интеграции 24xxx
  INTEGRATION_TYPE_INPUT_MISSING = 24400,
  INTEGRATION_NOT_FOUND = 24404,
  INTEGRATION_ALREADY_EXISTS = 24409,
  INTEGRATION_CONNECTION_FAILED = 24422,

  MOBILE_FEATURE_NOT_FOUND = 25404,
}
